import React, { useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/constants/featureRegistry";
import { useOrderAttention } from "@/context/OrderAttentionContext";
import { Button } from "@/components/ui/button";
import { formatRp } from "@/lib/api";
import { REFUND_LABEL, refundOf } from "@/lib/orderCancellation";
import { RefundProcessingDialog, RefundCompleteDialog } from "@/components/admin/orders/RefundActionDialogs";
import {
  useServerOrderList, OpsTabs, OpsSearch, OpsPagination, OrderCell, CustomerCell, PaymentCell, StatusPill, EmptyRow, fmtDateTime,
} from "@/components/admin/orders/OrderOpsList";

const TABS = [
  { key: "pending", value: "PENDING", label: "Menunggu" },
  { key: "processing", value: "PROCESSING", label: "Sedang Diproses" },
  { key: "refunded", value: "REFUNDED", label: "Selesai" },
];

function RefundMeta({ o }) {
  const r = refundOf(o);
  const l = REFUND_LABEL[r.status] || REFUND_LABEL.PENDING;
  const raw = o.refund || {};
  return (
    <div className="text-xs space-y-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill label={l.admin} cls={l.cls} testId={`ops-refund-status-${o.order_number}`} />
        <span className="font-display font-bold text-sm" data-testid={`ops-refund-amount-${o.order_number}`}>{formatRp(r.amount)}</span>
      </div>
      <div className="text-[#5C5C5C]">Dibatalkan {fmtDateTime(o.cancellation?.at)}{o.cancellation?.reason ? ` · “${o.cancellation.reason}”` : ""}</div>
      {r.status === "PROCESSING" && <div className="text-[#5C5C5C]">Mulai diproses {fmtDateTime(raw.processing_at)}</div>}
      {r.status === "REFUNDED" && (
        <div className="text-[#5C5C5C] break-words" data-testid={`ops-refund-detail-${o.order_number}`}>
          Selesai {fmtDateTime(r.refunded_at)} · {r.method || "-"}{r.reference ? ` · Ref: ${r.reference}` : ""}{raw.reason ? ` · ${raw.reason}` : ""}
        </div>
      )}
    </div>
  );
}

function RowActions({ o, canAct, onProcessing, onComplete }) {
  const st = refundOf(o).status;
  if (st === "REFUNDED") return <Link to={`/admin/pesanan/${o.id}`} className="text-xs font-bold text-[#E85D04]" data-testid={`ops-detail-${o.order_number}`}>Detail →</Link>;
  if (!canAct) return <span className="text-xs text-[#5C5C5C]" data-testid={`ops-readonly-${o.order_number}`}>Hanya lihat</span>;
  if (st === "PENDING") return <Button size="sm" className="rounded-lg brand-btn-primary" onClick={() => onProcessing(o)} data-testid={`ops-refund-processing-${o.order_number}`}>Tandai Diproses</Button>;
  return <Button size="sm" className="rounded-lg brand-btn-primary" onClick={() => onComplete(o)} data-testid={`ops-refund-complete-${o.order_number}`}>Catat Refund Selesai</Button>;
}

export default function AdminOrderRefunds() {
  const { user } = useAuth();
  const canAct = hasPermission(user, "orders.update");
  const { data: attention, refresh: refreshAttention } = useOrderAttention();
  const list = useServerOrderList({ filterKey: "refund_status", tabs: TABS, defaultTab: "pending" });
  const [processingOf, setProcessingOf] = useState(null);
  const [completeOf, setCompleteOf] = useState(null);
  const done = () => { setProcessingOf(null); setCompleteOf(null); list.reload(); refreshAttention(); };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl font-black" data-testid="refunds-title">Refund</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Kewajiban refund manual (bank/QRIS) dari pesanan yang dibatalkan setelah pembayaran terverifikasi. Alur: Menunggu → Sedang Diproses → Selesai. Dynicty tidak mentransfer dana.</p>
        </div>
        <Link to="/admin/pesanan" className="text-xs font-bold text-[#E85D04]" data-testid="refunds-all-orders-link">Semua pesanan →</Link>
      </div>
      <OpsTabs tabs={TABS} value={list.tabKey} onChange={list.setTab} counts={{ pending: attention?.refund_pending || 0 }} testPrefix="refunds" />

      <div className="bg-white border border-[#E5E5E5] rounded-2xl mt-4" data-testid="refunds-list">
        <div className="p-4 border-b border-[#E5E5E5]"><OpsSearch value={list.q} onChange={list.setQ} testId="refunds-search" /></div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">No Pesanan</th><th className="px-4 py-3">Pelanggan</th><th className="px-4 py-3">Pembayaran</th>
                <th className="px-4 py-3">Refund</th><th className="px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6] align-top" data-testid={`ops-row-${o.order_number}`}>
                  <td className="px-4 py-3"><OrderCell o={o} /></td>
                  <td className="px-4 py-3"><CustomerCell o={o} /></td>
                  <td className="px-4 py-3"><PaymentCell o={o} /></td>
                  <td className="px-4 py-3"><RefundMeta o={o} /></td>
                  <td className="px-4 py-3"><RowActions o={o} canAct={canAct} onProcessing={setProcessingOf} onComplete={setCompleteOf} /></td>
                </tr>
              ))}
              {list.items.length === 0 && <EmptyRow loading={list.loading} colSpan={5} text="Tidak ada refund pada tab ini" />}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-[#E5E5E5]" data-testid="refunds-mobile-list">
          {list.items.map((o) => (
            <div key={o.id} className="p-4 space-y-3" data-testid={`ops-card-${o.order_number}`}>
              <OrderCell o={o} />
              <div className="grid grid-cols-2 gap-3"><CustomerCell o={o} /><PaymentCell o={o} /></div>
              <RefundMeta o={o} />
              <RowActions o={o} canAct={canAct} onProcessing={setProcessingOf} onComplete={setCompleteOf} />
            </div>
          ))}
          {list.items.length === 0 && <div className="py-12 text-center text-sm text-[#5C5C5C]">{list.loading ? "Memuat…" : "Tidak ada refund pada tab ini"}</div>}
        </div>
        <OpsPagination page={list.page} total={list.total} onPage={list.setPage} testPrefix="refunds" />
      </div>

      <RefundProcessingDialog open={!!processingOf} order={processingOf} onClose={() => setProcessingOf(null)} onDone={done} />
      <RefundCompleteDialog open={!!completeOf} order={completeOf} onClose={() => setCompleteOf(null)} onDone={done} />
    </AdminLayout>
  );
}
